// Fixtures for the Duplo AI extension API, captured from baic-dev on 2026-08-18.
// Swap these out for real API calls when the read-only DuploAI client exists.
//
// Shaped as the *consumed subset* of each response, not the raw envelope. The real
// responses are paginated ({ success, data: { items, page, pageSize, totalCount,
// hasNextPage } }), so the client must page — these fixtures stand in for the
// already-collected items.
//
// `spec.kubeconfig` is deliberately absent. The live baic-infra response carries a
// full cluster-admin kubeconfig for on-prem infras (and only for on-prem — cloud
// infras authenticate through cloud credentials). The client drops it at the parse
// boundary, so nothing downstream, fixtures included, should ever model it.
//
// This environment moves. Over a single day `aon` was recreated under a new id, its
// desired version went 1.0.13-11 -> 1.0.13-27, dh-uae reached 1.0.13-30, and a
// `dev01` tenant appeared and vanished. Treat these values as a snapshot.

/** A DuploAI workspace. On-prem clusters share one workspace, so this does not identify a cluster. */
export interface DuploAiWorkspace {
  id: string;
  name: string;
}

/** Lifecycle status reported by the extension's Terraform runs. */
export type DuploAiStatus = 'Complete' | 'Failed' | 'Processing' | 'WaitingForApproval';

/**
 * A BAIC infra — one cluster. This is the unit a Sunstone deployment maps to.
 * Cluster identity is `name` (spec.infraName on tenants), NOT a scope id: every
 * tenant in baic-dev shares duploScopeId 6a46632f9d37d6669b118b38 regardless of
 * provider, so scope cannot distinguish clusters.
 */
export interface DuploAiInfra {
  id: string;
  name: string;
  cloudProvider: 'onprem' | 'aws' | 'azure';
  status: DuploAiStatus;
}

/**
 * A BAIC tenant — one Sunstone workspace.
 *
 * `deployedChartVersion` is `result.chartVersion`: the release pin that actually
 * applied. It is absent until an apply completes, which is why it is optional
 * rather than defaulted. `desiredChartVersion` (`spec.chartVersion`) is the
 * promote lever — editing it plus a lastRequestedAction is how a version gets
 * installed — but it is never shown as "the version a cluster is running".
 *
 * Both are release pins, not per-service chart versions. One pin expands to many
 * helm releases (dh-uae reports 61 enabled services, each its own
 * duploai_helm_release). Per-service chart-name/chart-version live in Sunstone's
 * own release manifest, not in this API.
 *
 * `releaseStatus` is Flux's view of the Helm release. `status` plus
 * `lastPlanSummary` are Terraform's, taken verbatim from the tenant's `status`
 * and `subStatus`. They disagree in practice, so they stay separate.
 *
 * `result.lastPlanHasDiff` is deliberately NOT modelled. It is `false` for every
 * tenant in baic-dev — including `aon`, whose own subStatus reads "Plan: 7 to
 * add, 7 to change, 4 to destroy" — so it is an unset default rather than an
 * observation, and rendering it as "no drift" states something untrue. Per-action
 * `hasDiff` is trustworthy, but only when a `plan` action exists, and `aon` has
 * only failed applies.
 */
export interface DuploAiTenant {
  id: string;
  name: string;
  infraName: string;
  cloudProvider: 'onprem' | 'aws' | 'azure';
  status: DuploAiStatus;
  deployedChartVersion?: string;
  desiredChartVersion?: string;
  releaseStatus?: string;
  lastPlanSummary?: string;
  /**
   * Consecutive failures at the END of the action history, not total failures.
   * dh-uae has 37 failures across 138 actions and is currently healthy, so a
   * total would mislead; a run of failures at the tail is what says "stuck".
   */
  consecutiveFailures?: number;
  /** The kind of the most recent action: a failed destroy means something different from a failed apply. */
  lastActionType?: 'apply' | 'plan' | 'destroy';
  /** When the most recent action finished, ISO 8601. */
  lastActionAt?: string;
  namespaceName?: string;
  releaseName?: string;
  enabledServiceCount?: number;
}

export const MOCK_DUPLOAI_WORKSPACES: DuploAiWorkspace[] = [
  { id: '69c629e2bd1fcee6fe046d48', name: 'DevOps' },
  { id: '69dac4b4492cb28da911b442', name: 'xforge-installer' },
  { id: '6a3ce59c8560ffc16bf8abb1', name: 'monitoring' },
];

/** The 7 infras in the xforge-installer workspace. Exactly one is on-prem. */
export const MOCK_DUPLOAI_INFRAS: DuploAiInfra[] = [
  {
    id: '6a6b01b32705b3602c398b24',
    name: 'devwest2-0730',
    cloudProvider: 'aws',
    status: 'Failed',
  },
  {
    id: '6a71bc90e3175026323a5a90',
    name: 'aon-rackspace',
    cloudProvider: 'onprem',
    status: 'Complete',
  },
  {
    id: '6a7230d0ae4cca30a3968736',
    name: 'baic-dev02',
    cloudProvider: 'azure',
    status: 'WaitingForApproval',
  },
  {
    id: '6a7931955c2c2bd8abcc2bc1',
    name: 'baic-uae',
    cloudProvider: 'azure',
    status: 'Complete',
  },
  {
    id: '6a7afd86024070a5ec16a69c',
    name: 'baic-aws',
    cloudProvider: 'aws',
    status: 'Failed',
  },
  {
    id: '6a7b388a024070a5ec1c806c',
    name: 'test-aws',
    cloudProvider: 'aws',
    status: 'Failed',
  },
  {
    id: '6a7d565c32084134bf6289a1',
    name: 'baic-dev01',
    cloudProvider: 'azure',
    status: 'Complete',
  },
];

/**
 * The 3 tenants in the xforge-installer workspace. Each covers a state the UI has
 * to survive:
 *   aon        — on-prem, a desired pin but nothing applied, and no release status
 *   dh-uae     — fully populated, the happy path
 *   agent-sbox — near-empty result: no version, no release status, no namespace
 */
export const MOCK_DUPLOAI_TENANTS: DuploAiTenant[] = [
  {
    id: '6a84a8ce172b6d2dade45683',
    name: 'aon',
    infraName: 'aon-rackspace',
    cloudProvider: 'onprem',
    status: 'Failed',
    desiredChartVersion: '1.0.13-27',
    lastPlanSummary: 'Plan: 7 to add, 7 to change, 4 to destroy.',
    consecutiveFailures: 4,
    lastActionType: 'apply',
    lastActionAt: '2026-08-19T05:22:06Z',
  },
  {
    id: '6a7d180732084134bf5c84bf',
    name: 'dh-uae',
    infraName: 'baic-uae',
    cloudProvider: 'azure',
    status: 'Complete',
    deployedChartVersion: '1.0.13-30',
    desiredChartVersion: '1.0.13-30',
    releaseStatus: 'Complete',
    lastPlanSummary: 'Apply complete! Resources: 1 added, 2 changed, 1 destroyed.',
    consecutiveFailures: 0,
    lastActionType: 'apply',
    lastActionAt: '2026-08-19T06:29:31Z',
    namespaceName: 'dh-uae',
    releaseName: 'baic',
    enabledServiceCount: 61,
  },
  {
    id: '6a82a679524b8c3ac6f784ce',
    name: 'agent-sbox',
    infraName: 'baic-uae',
    cloudProvider: 'azure',
    status: 'Failed',
    lastPlanSummary: 'Plan: 0 to add, 0 to change, 4 to destroy.',
    consecutiveFailures: 1,
    lastActionType: 'destroy',
    lastActionAt: '2026-08-17T08:59:45Z',
  },
];

/** On-prem infras only — the clusters Sunstone can import. */
export function onPremInfras(infras: DuploAiInfra[] = MOCK_DUPLOAI_INFRAS): DuploAiInfra[] {
  return infras.filter((i) => i.cloudProvider === 'onprem');
}

/** Tenants belonging to a cluster, matched on infra name. These become workspaces. */
export function tenantsForInfra(
  infraName: string,
  tenants: DuploAiTenant[] = MOCK_DUPLOAI_TENANTS,
): DuploAiTenant[] {
  return tenants.filter((t) => t.infraName === infraName);
}
